import React, { ChangeEvent, useEffect, useState } from "react";
import { Rule } from "antd/es/form";

import { ReactComponent as Power1 } from "src/static/icons/icon-power-1.svg";
import { ReactComponent as Power2 } from "src/static/icons/icon-power-2.svg";
import { ReactComponent as Power3 } from "src/static/icons/icon-power-3.svg";
import { ReactComponent as Power4 } from "src/static/icons/icon-power-4.svg";
import InfoModal from "@components/modals/InfoModal";
import BasicDivider from "@components/divider/BasicDivider";
import InfoButton from "@components/buttons/InfoButton";
import { Radio, RadioChangeEvent, Select } from "antd";
import { useTranslation } from "react-i18next";
import { ResourceInputItem } from "@type/estimateInput/estimateInput.response";
import { InputField } from "@type/estimateInput/estimateInput.dto";
import { ReactComponent as Bin } from "../../static/icons/icon-bin.svg";
import HTMLReactParser from "html-react-parser";
import InputNumeric from "@components/inputs/InputNumeric";

interface ResourceFormFieldProps {
  value?: InputField;
  onChange?: (val: InputField) => void;
  onDelete?: (key: string) => void;
  resource: ResourceInputItem;
  mustBeChecked?: boolean;
  showLabel?: boolean;
  module: string;
  required?: boolean;
  emptyErrorMessage?: string;
  readOnly?: boolean;
  fieldClassName?: string;
  maxLength?: number;
  addonAfter?: string;
  addonBefore?: string;
  className?: string;
  fieldPrefix?: string;
  rules?: Rule[];
  initialValue?: { [key in string]: string } | string;
  disabled?: boolean;
}

const ResourceFormField: React.FunctionComponent<ResourceFormFieldProps> = (
  props: ResourceFormFieldProps
) => {
  const {
    value,
    resource,
    readOnly,
    onChange,
    onDelete,
    maxLength,
    disabled,
    module,
  } = props;
  const { t } = useTranslation();

  const [inputValue, setInputValue] = useState<string | number>();
  const [inputUnit, setInputUnit] = useState<string>();

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const renderPotentialIcon = (value: number) => {
    switch (value) {
      case 1:
        return <Power1 className="me-2" aria-hidden="true" focusable="false" />;
      case 2:
        return <Power2 className="me-2" aria-hidden="true" focusable="false" />;
      case 3:
        return <Power3 className="me-2" aria-hidden="true" focusable="false" />;
      case 4:
        return <Power4 className="me-2" aria-hidden="true" focusable="false" />;
    }
    return <></>;
  };

  const getPotentialDescription = (value: number) => {
    switch (value) {
      case 1:
        return "Pouvoir méthanogène faible";
      case 2:
        return "Pouvoir méthanogène moyen";
      case 3:
        return "Pouvoir méthanogène élevé";
      case 4:
        return "Pouvoir méthanogène très élevé";
    }
    return <></>;
  };

  useEffect(() => {
    if (resource.type === "number") setInputValue(undefined);
    if (resource.units && resource.units.length > 0)
      setInputUnit(resource.units[0]);
  }, [resource]);

  useEffect(() => {
    if (value?.value) setInputValue(value.value);
    if (value?.unit) setInputUnit(value.unit);
  }, [value]);

  const onChangeUnit = (e: RadioChangeEvent) => {
    const newValue = {
      key: resource.key,
      value: inputValue ?? value?.value ?? undefined,
      unit: e.target.value,
    };
    if (onChange) onChange(newValue);
    setInputUnit(e.target.value);
  };

  const onChangeSelectValue = (val: string) => {
    const newValue = {
      key: resource.key,
      value: val,
      unit: inputUnit ?? value?.unit,
    };
    if (onChange) onChange(newValue);
    setInputValue(val);
  };

  // const onChangeValue = (val: number) => {
  //   const newValue = {
  //     key: resource.key,
  //     value: val,
  //     unit: inputUnit ?? value?.unit,
  //   };
  //   if (onChange) onChange(newValue);
  //   setInputValue(val);
  // };

  const onChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    const newValue = {
      key: resource.key,
      value: val,
      unit: inputUnit ?? value?.unit,
    };
    if (onChange) onChange(newValue);
    setInputValue(val);
  };
  //
  // const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
  //   const value = e.target.value;
  //   setLocalQuery(value);
  //   clearTimeout(searchTimeout.current);
  //   searchTimeout.current = window.setTimeout(() => setLocalQuery(value), 500);
  // };

  return (
    <div className="d-flex flex-column">
      <div className="d-flex flex-row mb-2 justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <InfoModal
            visible={isModalVisible}
            onOk={() => setIsModalVisible(false)}
            onCancel={() => setIsModalVisible(false)}
          >
            <div>
              {resource.potential && (
                <>
                  <div className="d-flex align-items-center">
                    {renderPotentialIcon(resource.potential)}
                    <div className="span-title ms-3">
                      {getPotentialDescription(resource.potential)}
                    </div>
                  </div>
                  <BasicDivider variant="grey" height={1} className={"w-100"} />
                </>
              )}
              <span className="span-title">
                {resource.infoTextTitle ?? resource.label}
              </span>
              <p className="mt-3 mb-0">
                {resource.infoText && HTMLReactParser(resource.infoText)}
              </p>
            </div>
          </InfoModal>
          {resource.potential && renderPotentialIcon(resource.potential)}
          <span className="span-title" id={`${resource.key}-label`}>
            {resource.label}
          </span>
          {resource.infoText && (
            <InfoButton
              onClick={() => {
                setIsModalVisible(true);
              }}
              className="text-primary"
            />
          )}
        </div>
        {onDelete && (
          <div
            onClick={() => {
              onDelete && onDelete(resource.key);
            }}
            className="button-icon"
            tabIndex={0}
          >
            <Bin width={20} height={20} />
          </div>
        )}
      </div>
      {resource.type === "number" && (
        <InputNumeric
          fieldKey={resource.key}
          key={resource.key}
          value={(value?.value as number) || (inputValue as number)}
          disabled={readOnly || disabled}
          className={readOnly ? "readonly-form-field" : ""}
          placeholder={resource.placeholder ? resource.placeholder : "0"}
          onChange={onChangeValue}
          // maxLength={maxLength}
          // type={resource.type ? resource.type : "text"}
          data-test-for={resource.key}
          minLength={0}
          maxLength={maxLength}
        />
      )}
      {resource.type === "enum" && resource.options && (
        <Select
          aria-labelledby={`${resource.key}-label`}
          options={[
            ...[{ value: "test", label: resource.placeholder }],
            ...Object.keys(resource.options)
              .sort()
              .map((key) => {
                return {
                  key: key,
                  value: key,
                  label: ((resource.options &&
                    (resource.options as { [key: string]: any })[key]) ??
                    "") as string,
                };
              }),
            // ...resource.options
          ]}
          placeholder={resource.placeholder}
          allowClear
          showSearch
          defaultActiveFirstOption
          onChange={onChangeSelectValue}
          value={(value?.value as string) || (inputValue as string)}
        />
      )}
      {module !== "collectivity" &&
        resource.units &&
        !(
          resource.units.length === 1 &&
          t(`input_units.${resource.units[0]}`) === null
        ) &&
        (resource.units.length > 1 ? (
          <Radio.Group
            disabled={readOnly || disabled}
            className={`d-flex justify-content-center mt-2 ${
              readOnly ? " readonly-form-field" : ""
            }`}
            onChange={onChangeUnit}
            value={value?.unit || inputUnit}
          >
            {resource.units.map((option, index) => (
              <Radio key={index} value={option}>
                {HTMLReactParser(t(`input_units.${option}`))}
              </Radio>
            ))}
          </Radio.Group>
        ) : (
          <span className="w-100 text-center mt-2">
            {HTMLReactParser(t(`input_units.${resource.units[0]}`))}
          </span>
        ))}
    </div>
  );
};

export default ResourceFormField;
